import React, { useState, useEffect } from 'react';
import { X, Circle, ArrowRight, CheckCircle, MinusCircle, Send } from 'lucide-react';
import toast from '../toast';
import dialogo from '../dialogo';

export function Spinner() {
  return <span className="spinner" />;
}

export function Empty({ icon, text = 'No hay datos.' }) {
  return (
    <div className="empty">
      <div className="icon">{icon || <Circle size={32} strokeWidth={1} color="var(--text-3)" />}</div>
      <p>{text}</p>
    </div>
  );
}

export function Alert({ type = 'ok', children }) {
  return <div className={`alert alert-${type}`}>{children}</div>;
}

export function StatusBadge({ estado }) {
  const config = {
    pendiente:          { label: 'Pendiente',            Icon: Circle,      className: 'badge-pendiente' },
    enviada_proveedor:  { label: 'Enviada a proveedor',  Icon: ArrowRight,  className: 'badge-enviada_proveedor' },
    respondida:         { label: 'Respondida',           Icon: CheckCircle, className: 'badge-respondida' },
    enviada_cliente:    { label: 'Enviada al cliente',   Icon: Send,        className: 'badge-enviada_cliente' },
    cerrada:            { label: 'Cerrada',              Icon: MinusCircle, className: 'badge-cerrada' },
  };
  const { label, Icon, className } = config[estado] || { label: estado, Icon: Circle, className: '' };
  return (
    <span className={`badge ${className}`}>
      <Icon size={11} strokeWidth={2} />
      {label}
    </span>
  );
}

// ── Nivel de cliente (segmento comercial del reporte de comprobantes) ────────
// El dato viene de NIVEL_CLIENTE y es la pauta para gestionar al cliente.
// A es el mejor segmento y S/C ("sin clasificar") el residual; el orden y los
// colores viven solo acá para que todos los módulos los muestren igual.
export const NIVELES = ['A', 'B', 'C', 'D', 'S/C'];

const NIVEL_ESTILOS = {
  'A':   { color: 'var(--green)',  bg: 'var(--green-dim)'  },
  'B':   { color: 'var(--blue)',   bg: 'var(--blue-dim)'   },
  'C':   { color: 'var(--amber)',  bg: 'var(--amber-dim)'  },
  'D':   { color: 'var(--orange)', bg: 'var(--orange-dim)' },
  'S/C': { color: 'var(--text-2)', bg: 'var(--surface-3)'  },
};

export function ordenNivel(nivel) {
  const i = NIVELES.indexOf(nivel);
  return i === -1 ? NIVELES.length : i;
}

export function NivelBadge({ nivel, size = 12, vacio = null }) {
  if (!nivel) return vacio;
  const estilo = NIVEL_ESTILOS[nivel] || { color: 'var(--text-2)', bg: 'var(--surface-3)' };
  return (
    <span
      title={`Nivel de cliente: ${nivel}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: nivel.length > 1 ? 30 : 20, height: 20, padding: '0 6px',
        borderRadius: 6, background: estilo.bg, color: estilo.color,
        fontSize: size, fontWeight: 700, lineHeight: 1,
        border: `1px solid ${estilo.color}`, flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {nivel}
    </span>
  );
}

export function Modal({ title, subtitle, onClose, footer, children }) {
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 6 }}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{title}</h1>
        {subtitle && <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsub = toast.subscribe(({ type, msg }) => {
      const id = Date.now() + Math.random();
      setToasts(prev => [...prev, { id, type, msg }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    });
    return unsub;
  }, []);

  if (!toasts.length) return null;

  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>
      ))}
    </div>
  );
}

/** Muestra los diálogos de `dialogo.js` (confirmar / pedir texto), de a uno. */
export function DialogoContainer() {
  const [cola, setCola]   = useState([]);
  const [texto, setTexto] = useState('');
  const actual = cola[0];

  useEffect(() => dialogo.subscribe(d => setCola(c => [...c, d])), []);
  useEffect(() => { if (actual) setTexto(actual.valorInicial || ''); }, [actual]);

  const cerrar = (valor) => {
    actual.resolve(valor);
    setCola(c => c.slice(1));
  };
  const esTexto = actual?.tipo === 'texto';
  const aceptar = () => {
    if (esTexto && !texto.trim()) return;
    cerrar(esTexto ? texto.trim() : true);
  };
  const cancelar = () => cerrar(esTexto ? null : false);

  // Esc cancela; Enter acepta (en el campo de texto lo maneja el input).
  useEffect(() => {
    if (!actual) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelar(); }
      else if (e.key === 'Enter' && !esTexto) { e.preventDefault(); e.stopPropagation(); aceptar(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  if (!actual) return null;
  return (
    // Por encima de los modales y del constructor de firmas (pantalla completa).
    <div className="overlay" style={{ zIndex: 1100 }} onMouseDown={e => e.target === e.currentTarget && cancelar()}>
      <div className="modal" role="alertdialog" aria-modal="true" style={{ maxWidth: 440 }}>
        <div className="modal-body" style={{ paddingBottom: 18 }}>
          {actual.titulo && <div style={{ fontWeight: 700, fontSize: 16, marginBottom: actual.mensaje || esTexto ? 8 : 0 }}>{actual.titulo}</div>}
          {actual.mensaje && <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{actual.mensaje}</div>}
          {esTexto && (
            <input className="form-control" autoFocus value={texto} maxLength={actual.maxLength}
                   placeholder={actual.placeholder} onChange={e => setTexto(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); aceptar(); } }}
                   style={{ marginTop: actual.mensaje ? 12 : 4 }} />
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-secondary" onClick={cancelar}>{actual.textoCancelar || 'Cancelar'}</button>
          <button className={`btn ${actual.peligro ? 'btn-danger' : 'btn-primary'}`} onClick={aceptar}
                  autoFocus={!esTexto} disabled={esTexto && !texto.trim()}>
            {actual.textoAceptar || 'Aceptar'}
          </button>
        </div>
      </div>
    </div>
  );
}
